import { useState, type FormEvent } from 'react';
import { authClient } from './auth-client.js';

type AuthMode = 'sign-in' | 'forgot' | 'reset' | 'invitation';

interface AuthScreenProps {
  readonly authenticated?: boolean;
  readonly authenticatedEmail?: string | undefined;
  readonly serviceUnavailable?: boolean;
}

interface InvitationAcceptanceResponse {
  readonly organizationId: string;
  readonly membershipId: string;
  readonly email: string;
  readonly verificationRequired: boolean;
  readonly accountCreated: boolean;
}

interface ApiErrorResponse {
  readonly code?: string;
  readonly requestId?: string;
  readonly retryAfter?: number;
}

const authenticationParameters = new URLSearchParams(window.location.search);
const invitationFragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
const resetToken = authenticationParameters.get('token');
let invitationToken = invitationFragment.get('invitation');

export const hasInvitationAction = Boolean(invitationToken);

if (invitationToken) {
  window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
}

function friendlyAuthError(message?: string): string {
  const normalized = message?.toLocaleLowerCase('en-US') ?? '';
  if (normalized.includes('invalid email or password') || normalized.includes('invalid password')) {
    return 'E-mail ou senha incorretos.';
  }
  if (normalized.includes('email not verified')) {
    return 'Verifique seu e-mail antes de entrar.';
  }
  if (normalized.includes('already exists') || normalized.includes('already registered')) {
    return 'Já existe uma conta com este e-mail.';
  }
  if (normalized.includes('expired') || normalized.includes('invalid token')) {
    return 'Este link expirou ou já foi utilizado. Solicite um novo.';
  }
  return 'Não foi possível concluir a solicitação. Tente novamente.';
}

export function AuthScreen({
  authenticated = false,
  authenticatedEmail,
  serviceUnavailable = false,
}: AuthScreenProps) {
  const [mode, setMode] = useState<AuthMode>(
    invitationToken ? 'invitation' : resetToken ? 'reset' : 'sign-in',
  );
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function continueInvitationWithInvitedAccount() {
    if (!invitationToken) {
      setMessage('Este convite não é válido. Peça um novo convite ao administrador.');
      return;
    }

    setIsSubmitting(true);
    setMessage(undefined);
    const preservedToken = invitationToken;
    try {
      const result = await authClient.signOut();
      if (result.error) {
        setMessage('Não foi possível encerrar a sessão atual. Tente novamente.');
        return;
      }
      window.location.replace(
        `${window.location.pathname}${window.location.search}#invitation=${encodeURIComponent(preservedToken)}`,
      );
    } catch {
      setMessage('Não foi possível encerrar a sessão atual. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);
    setMessage(undefined);

    try {
      if (mode === 'sign-in') {
        const result = await authClient.signIn.email({ email, password, rememberMe: true });
        if (result.error) {
          setMessage(friendlyAuthError(result.error.message));
          return;
        }
        window.location.replace('/');
        return;
      }

      if (mode === 'invitation') {
        if (!invitationToken) {
          setMessage('Este convite não é válido. Peça um novo convite ao administrador.');
          return;
        }

        const response = await fetch('/v1/invitations/accept', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: invitationToken,
            ...(authenticated ? {} : { name, password }),
          }),
        });
        if (!response.ok) {
          const error = (await response.json().catch(() => ({}))) as ApiErrorResponse;
          const supportCode = error.requestId ? ` Código de atendimento: ${error.requestId}.` : '';
          setMessage(
            response.status === 409
              ? authenticated
                ? 'Este convite pertence a outra conta ou não pode ser aceito nesta sessão. Entre com o e-mail convidado.'
                : 'Já existe uma conta com este e-mail. Entre primeiro e abra novamente o convite recebido.'
              : response.status === 404
                ? 'Este convite é inválido ou expirou. Peça um novo convite ao administrador.'
                : response.status === 429
                  ? `Foram feitas muitas tentativas. Aguarde ${Math.max(1, Math.ceil((error.retryAfter ?? 60) / 60))} minuto(s) e tente novamente.`
                  : response.status === 422
                    ? 'Não foi possível criar a conta com os dados informados. Confira o nome e escolha outra senha.'
                    : response.status >= 500
                      ? `O sistema encontrou uma falha interna, mas seu convite continua válido. Tente novamente em instantes.${supportCode}`
                      : 'Não foi possível aceitar o convite. Confira os dados informados e tente novamente.',
          );
          return;
        }

        const result = (await response.json()) as InvitationAcceptanceResponse;
        window.history.replaceState({}, '', window.location.pathname);
        invitationToken = null;
        if (!result.accountCreated) {
          window.location.replace('/');
          return;
        }
        setMode('sign-in');
        setName('');
        setEmail(result.email);
        setPassword('');
        setMessage(
          result.verificationRequired
            ? 'Convite aceito. Verifique seu e-mail e depois entre na Aeronexo.'
            : 'Convite aceito. Entre com o e-mail que recebeu o convite.',
        );
        return;
      }

      if (mode === 'forgot') {
        const result = await authClient.requestPasswordReset({
          email,
          redirectTo: `${window.location.origin}/?reset=password`,
        });
        setMessage(
          result.error
            ? friendlyAuthError(result.error.message)
            : 'Se o endereço estiver cadastrado, você receberá as instruções de recuperação.',
        );
        return;
      }

      if (!resetToken) {
        setMessage('O link de recuperação não é válido. Solicite um novo link.');
        return;
      }

      const result = await authClient.resetPassword({ newPassword: password, token: resetToken });
      if (result.error) {
        setMessage(friendlyAuthError(result.error.message));
        return;
      }

      window.history.replaceState({}, '', window.location.pathname);
      setMode('sign-in');
      setPassword('');
      setMessage('Senha atualizada. Entre novamente com sua nova senha.');
    } catch {
      setMessage(
        'Não foi possível concluir a solicitação. Verifique sua conexão e tente novamente.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  const title =
    mode === 'invitation'
      ? authenticated
        ? 'Escolha a conta do convite'
        : 'Aceitar convite'
      : mode === 'forgot'
        ? 'Recuperar acesso'
        : mode === 'reset'
          ? 'Definir nova senha'
          : 'Acessar a operação';

  const description =
    mode === 'invitation'
      ? authenticated
        ? 'Este navegador já tem uma sessão aberta. Escolha abaixo como deseja continuar.'
        : 'Confirme seu nome e defina uma senha. O e-mail e a organização vêm do convite protegido.'
      : mode === 'forgot'
        ? 'Informe seu e-mail para receber um link seguro de recuperação.'
        : mode === 'reset'
          ? 'Escolha uma nova senha para voltar ao seu ambiente.'
          : 'Entre com seu e-mail e senha para continuar.';

  const changeMode = (nextMode: AuthMode) => {
    if (mode === 'invitation' && nextMode !== 'invitation') invitationToken = null;
    setMode(nextMode);
    setMessage(undefined);
    setPassword('');
    setShowPassword(false);
  };

  return (
    <main className="authPage" id="main-content">
      <section className="authIdentity" aria-label="Aeronexo Coordenação de Voo">
        <a className="brand" href="/" aria-label="Aeronexo — página inicial">
          <span className="brandMark" aria-hidden="true">
            A
          </span>
          <span>
            <strong>Aeronexo</strong>
            <small>Coordenação de Voo</small>
          </span>
        </a>
        <div className="authValueProposition">
          <span className="authEnvironment">Coordenação de voo</span>
          <h1>
            Cada voo.
            <br />
            Todos na mesma página.
          </h1>
          <p>Planeje a operação, acompanhe as pendências e dê continuidade ao próximo turno.</p>
          <div className="authJourney" aria-label="Fluxo de trabalho">
            <span>
              <b>01</b>Planejar
            </span>
            <i aria-hidden="true" />
            <span>
              <b>02</b>Coordenar
            </span>
            <i aria-hidden="true" />
            <span>
              <b>03</b>Acompanhar
            </span>
          </div>
        </div>
        <small>Aeronexo · Coordenação de Voo</small>
      </section>

      <section className="authPanel">
        <div className="authCard">
          <span className="authCardEyebrow">Bem-vindo à Aeronexo</span>
          <h2>{title}</h2>
          <p className="authIntro">{description}</p>

          {serviceUnavailable ? (
            <div className="authNotice" role="status">
              Não foi possível confirmar o serviço de autenticação. Tente novamente em instantes.
            </div>
          ) : null}

          {mode === 'invitation' && authenticated ? (
            <div className="invitationSessionNotice" role="status">
              <strong>Há uma conta aberta neste navegador</strong>
              <span>
                Você está conectado como <b>{authenticatedEmail ?? 'outro usuário'}</b>. O convite
                só será aceito se tiver sido enviado para esse mesmo e-mail.
              </span>
            </div>
          ) : null}

          <form onSubmit={(event) => void handleSubmit(event)}>
            {mode === 'invitation' && !authenticated ? (
              <label>
                Nome completo
                <input
                  autoComplete="name"
                  onChange={(event) => setName(event.target.value)}
                  pattern="[^@]+"
                  required
                  title="Informe seu nome completo, sem endereço de e-mail."
                  value={name}
                />
                <small>Informe seu nome; o e-mail já está definido pelo convite.</small>
              </label>
            ) : null}

            {mode !== 'reset' && mode !== 'invitation' ? (
              <label>
                E-mail
                <input
                  autoComplete="email"
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  type="email"
                  value={email}
                />
              </label>
            ) : null}

            {mode === 'sign-in' || (mode === 'invitation' && !authenticated) || mode === 'reset' ? (
              <label>
                {mode === 'reset' ? 'Nova senha' : 'Senha'}
                <span className="passwordField">
                  <input
                    autoComplete={mode === 'sign-in' ? 'current-password' : 'new-password'}
                    minLength={8}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                  />
                  <button
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    type="button"
                    onClick={() => setShowPassword((visible) => !visible)}
                  >
                    {showPassword ? 'Ocultar' : 'Mostrar'}
                  </button>
                </span>
                {mode !== 'sign-in' ? <small>Mínimo de 8 caracteres.</small> : null}
              </label>
            ) : null}

            {message ? (
              <div className="authMessage" role="status">
                {message}
              </div>
            ) : null}

            <button
              className={authenticated ? 'invitationCurrentAccount' : 'primaryButton authSubmit'}
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting
                ? 'Processando…'
                : mode === 'invitation'
                  ? authenticated
                    ? `Aceitar com ${authenticatedEmail ?? 'a conta atual'}`
                    : 'Aceitar convite'
                  : mode === 'forgot'
                    ? 'Enviar instruções'
                    : mode === 'reset'
                      ? 'Atualizar senha'
                      : 'Entrar'}
            </button>
          </form>

          {mode === 'invitation' && authenticated ? (
            <button
              className="primaryButton invitationSwitchAccount"
              disabled={isSubmitting}
              type="button"
              onClick={() => void continueInvitationWithInvitedAccount()}
            >
              {isSubmitting ? 'Encerrando sessão…' : 'Sair e usar o e-mail convidado'}
            </button>
          ) : null}

          {mode === 'sign-in' ? (
            <div className="authInvitationOnly" role="note">
              <strong>Ainda não tem acesso?</strong>
              <span>Solicite um convite ao administrador da sua organização.</span>
            </div>
          ) : null}

          <div className="authAlternatives">
            {mode === 'sign-in' ? (
              <button type="button" onClick={() => changeMode('forgot')}>
                Esqueci minha senha
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (authenticated) {
                    invitationToken = null;
                    window.location.replace('/');
                    return;
                  }
                  changeMode('sign-in');
                }}
              >
                {authenticated ? 'Voltar ao painel' : 'Voltar para a entrada'}
              </button>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
